import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth, setupRecaptcha, signInWithPhoneNumber } from '../firebase';

const Login = () => {
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Phone Auth States
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { login, firebaseLogin } = useAuth();
  const navigate = useNavigate();
  const location  = useLocation();
  // If user was sent here from a protected page (e.g. /donate), go back there after login
  const redirectTo = location.state?.from || '/dashboard';

  useEffect(() => {
    // Setup reCAPTCHA when switching to phone login method
    if (loginMethod === 'phone') {
      setupRecaptcha('recaptcha-container');
    }
  }, [loginMethod]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
    
    // Clear specific field validation error
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEmailForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'ईमेल आवश्यक है';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'वैध ईमेल पता दर्ज करें';
    }
    if (!formData.password) {
      errors.password = 'पासवर्ड आवश्यक है';
    } else if (formData.password.length < 6) {
      errors.password = 'पासवर्ड कम से कम 6 अक्षर का होना चाहिए';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailForm()) return;
    
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('लॉगिन में त्रुटि हुई। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = async (e) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('कृपया वैध मोबाइल नंबर दर्ज करें');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Format number to international format if not already (assuming India +91)
      const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
      const appVerifier = window.recaptchaVerifier;
      
      const confirmation = await signInWithPhoneNumber(auth, formattedNumber, appVerifier);
      setConfirmationResult(confirmation);
      setOtpSent(true);
    } catch (error) {
      console.error('Error during signInWithPhoneNumber:', error);
      setError('OTP भेजने में विफल। कृपया पुनः प्रयास करें।');
      // Reset recaptcha if failed
      if (window.recaptchaVerifier) window.recaptchaVerifier.render().then(widgetId => window.grecaptcha.reset(widgetId));
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('कृपया 6 अंकों का OTP दर्ज करें');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await confirmationResult.confirm(otp);
      // Get Firebase ID token
      const idToken = await result.user.getIdToken();
      
      // Send token to our backend
      const loginResult = await firebaseLogin(idToken);
      
      if (loginResult.success) {
        navigate(redirectTo, { replace: true });
      } else {
        setError(loginResult.message);
      }
    } catch (error) {
      console.error('Error confirming OTP:', error);
      setError('अमान्य OTP। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-temple-bg flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="text-4xl sm:text-6xl mb-4">🙏</div>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text">
            लॉगिन करें
          </h2>
          <p className="mt-2 text-temple-text-muted text-sm sm:text-base">
            अपने खाते में प्रवेश करें
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-temple-bg-warm rounded-3xl shadow-temple p-6 sm:p-8 border border-temple-primary/10">
          
          {/* Tabs */}
          <div className="flex mb-6 border-b border-temple-primary/20">
            <button
              onClick={() => setLoginMethod('email')}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${
                loginMethod === 'email' 
                  ? 'text-temple-primary border-b-2 border-temple-primary' 
                  : 'text-temple-text-muted hover:text-temple-text'
              }`}
            >
              ईमेल से लॉगिन
            </button>
            <button
              onClick={() => setLoginMethod('phone')}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${
                loginMethod === 'phone' 
                  ? 'text-temple-primary border-b-2 border-temple-primary' 
                  : 'text-temple-text-muted hover:text-temple-text'
              }`}
            >
              मोबाइल नंबर (OTP)
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}

          {loginMethod === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-temple-text mb-2">
                  ईमेल पता
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full p-3 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                    validationErrors.email 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-temple-primary/20 focus:border-temple-primary'
                  }`}
                  placeholder="आपका ईमेल पता"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-temple-text mb-2">
                  पासवर्ड
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full p-3 pr-12 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                      validationErrors.password 
                        ? 'border-red-300 focus:border-red-500' 
                        : 'border-temple-primary/20 focus:border-temple-primary'
                    }`}
                    placeholder="आपका पासवर्ड"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-temple-text-muted hover:text-temple-primary"
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-temple-primary focus:ring-temple-primary border-temple-primary/30 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-temple-text-muted">
                    मुझे याद रखें
                  </label>
                </div>
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-temple-primary hover:text-temple-primary-dark font-medium">
                    पासवर्ड भूल गए?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-temple-primary py-3 px-4 rounded-xl font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'लॉगिन हो रहा है...' : 'लॉगिन करें'}
              </button>
            </form>
          ) : (
            <form onSubmit={otpSent ? verifyOTP : requestOTP} className="space-y-6">
              {!otpSent ? (
                <div>
                  <label className="block text-sm font-bold text-temple-text mb-2">
                    मोबाइल नंबर
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border-2 border-r-0 border-temple-primary/20 bg-temple-bg-soft text-temple-text-muted font-bold">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        setError('');
                      }}
                      className="w-full p-3 border-2 rounded-r-lg focus:ring-0 transition bg-temple-bg border-temple-primary/20 focus:border-temple-primary"
                      placeholder="आपका 10 अंकों का नंबर"
                      maxLength="10"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-bold text-temple-text mb-2">
                    OTP दर्ज करें
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value);
                      setError('');
                    }}
                    className="w-full p-3 border-2 rounded-lg focus:ring-0 transition bg-temple-bg text-center tracking-widest text-lg font-bold border-temple-primary/20 focus:border-temple-primary"
                    placeholder="------"
                    maxLength="6"
                  />
                  <div className="mt-2 text-right">
                    <button 
                      type="button" 
                      onClick={() => setOtpSent(false)}
                      className="text-sm text-temple-primary hover:text-temple-primary-dark"
                    >
                      नंबर बदलें
                    </button>
                  </div>
                </div>
              )}

              {/* Invisible reCAPTCHA container */}
              <div id="recaptcha-container"></div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-temple-primary py-3 px-4 rounded-xl font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'कृपया प्रतीक्षा करें...' : (otpSent ? 'OTP सत्यापित करें' : 'OTP भेजें')}
              </button>
            </form>
          )}

          <div className="text-center mt-6">
            <p className="text-sm text-temple-text-muted">
              खाता नहीं है?{' '}
              <Link to="/signup" className="text-temple-primary hover:text-temple-primary-dark font-bold">
                साइनअप करें
              </Link>
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="bg-temple-bg-soft rounded-2xl p-4 sm:p-6 border border-temple-secondary/20">
          <h3 className="text-base sm:text-lg font-bold text-temple-text mb-4 text-center">
            सदस्यता के लाभ
          </h3>
          <ul className="space-y-2 text-sm text-temple-text-muted">
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              व्यक्तिगत डैशबोर्ड
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              दान का इतिहास देखें
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              विशेष कार्यक्रमों की जानकारी
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              प्राथमिकता सहायता
            </li>
          </ul>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="text-temple-primary hover:text-temple-primary-dark font-medium text-sm inline-flex items-center"
          >
            <span className="mr-1">←</span>
            होम पेज पर वापस जाएं
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;